import client from './inventory-request';
import { withStoreCreateBody, withStoreUpdateBody, getStoreActorId } from '../hooks/useStoreScope';
import {
  toRfqApiItemBody,
  toRfqItemUpdateBody,
  lineNeedsUpdate,
  normalizeApiList,
} from '../utils/rfqEntity';

// RFQs — official spec #16–#28 (proxied via EMR /inventory/rfqs/...)

export const getRfqs = (p) => client.get('/inventory/rfqs/', p);
export const getRfq = (id) => client.get(`/inventory/rfqs/${id}/`);
export const createRfq = (body) => client.post('/inventory/rfqs/', withStoreCreateBody(body));
export const updateRfq = (id, body) => client.patch(`/inventory/rfqs/${id}/`, withStoreUpdateBody(body));

export const getRfqItems = (id) => client.get(`/inventory/rfqs/${id}/items/`);
export const addRfqItem = (rfqId, body) =>
  client.post(`/inventory/rfqs/${rfqId}/items/`, withStoreCreateBody(body));

// Flat rfq-items routes (not nested under /rfqs/{id}/)
export const getRfqItem = (itemId, scope = {}) =>
  client.get(`/inventory/rfq-items/${itemId}/`, scope);
export const updateRfqItem = (itemId, body) =>
  client.patch(`/inventory/rfq-items/${itemId}/`, withStoreUpdateBody(body));
export const deleteRfqItem = (itemId, scope = {}) =>
  client.delete(`/inventory/rfq-items/${itemId}/`, scope);

export const getRfqSummary = (id) => client.get(`/inventory/rfqs/${id}/summary/`);
export const publishRfq = (id) => client.post(`/inventory/rfqs/${id}/publish/`, {});
export const closeRfq = (id) => client.post(`/inventory/rfqs/${id}/close/`, {});
export const cancelRfq = (id) => client.post(`/inventory/rfqs/${id}/cancel/`, {});

export const getRfqQuotes = (id) => client.get(`/inventory/rfqs/${id}/quotes/`);
export const getRfqQuoteComparison = (id) => client.get(`/inventory/rfqs/${id}/quote-comparison/`);
export const getRfqEvaluationSummary = (id) => client.get(`/inventory/rfqs/${id}/evaluation-summary/`);

/** Legacy fallback if backend still exposes flat rfq-vendors list */
export const getRfqVendors = (rfqIdOrParams) =>
  client.get('/inventory/rfq-vendors/', typeof rfqIdOrParams === 'object' ? rfqIdOrParams : { rfq_id: rfqIdOrParams });

/** Sync line items on DRAFT edit — PATCH existing, DELETE removed, POST new */
export async function syncRfqItems(rfqId, originalLines, editedLines, submissionDeadline) {
  const origList = originalLines || [];
  const editedList = (editedLines || []).filter((l) => l.item_id && l.required_quantity);
  const origById = new Map(origList.filter((l) => l.id).map((l) => [l.id, l]));
  const editedIds = new Set(editedList.filter((l) => l.id).map((l) => l.id));

  await Promise.all(
    origList
      .filter((l) => l.id && !editedIds.has(l.id))
      .map((l) => deleteRfqItem(l.id)),
  );

  await Promise.all(
    editedList.map(async (line) => {
      if (line.id) {
        const orig = origById.get(line.id);
        if (orig && lineNeedsUpdate(orig, line)) {
          await updateRfqItem(line.id, toRfqItemUpdateBody(line));
        }
        return;
      }
      await addRfqItem(rfqId, toRfqApiItemBody(line, submissionDeadline));
    }),
  );
}

export async function createRfqWithItemsAndVendors(formPayload, lines, { publish = false } = {}) {
  const rfq = await createRfq(formPayload);
  const rfqId = rfq?.id || rfq?.rfq_id;
  if (!rfqId) throw new Error('RFQ created but no ID returned');

  const validLines = (lines || []).filter((l) => l.item_id && l.required_quantity);
  if (validLines.length > 0) {
    await Promise.all(
      validLines.map((line) => addRfqItem(rfqId, toRfqApiItemBody(line, formPayload.submission_deadline))),
    );
  }

  if (publish) {
    const published = await publishRfq(rfqId);
    return { ...rfq, ...published, id: rfqId, rfq_id: rfqId };
  }
  return { ...rfq, id: rfqId, rfq_id: rfqId };
}

export async function updateRfqWithItemsAndVendors(
  rfqId,
  formPayload,
  lines,
  originalLines,
  { publish = false } = {},
) {
  await updateRfq(rfqId, formPayload);
  await syncRfqItems(rfqId, originalLines, lines, formPayload.submission_deadline);

  if (publish) {
    const published = await publishRfq(rfqId);
    return { ...published, id: rfqId, rfq_id: rfqId };
  }
  const updated = await getRfq(rfqId);
  return { ...updated, id: rfqId, rfq_id: rfqId };
}

// Vendor quotes — Section 4 (vendor-bidding-service)
export const getVendorQuotes = (p) => client.get('/inventory/vendor-quotes/', p);
export const getVendorQuote = (id) => client.get(`/inventory/vendor-quotes/${id}/`);
export const createVendorQuote = (body) => client.post('/inventory/vendor-quotes/', withStoreCreateBody(body));
export const updateVendorQuote = (id, body) =>
  client.patch(`/inventory/vendor-quotes/${id}/`, withStoreUpdateBody(body));

export const addVendorQuoteItem = (quoteId, body) =>
  client.post(`/inventory/vendor-quotes/${quoteId}/items/`, withStoreCreateBody(body));
export const getVendorQuoteItemsByQuote = (quoteId) =>
  client.get(`/inventory/vendor-quotes/${quoteId}/items/`);

export const getVendorQuoteItems = (p) => client.get('/inventory/vendor-quote-items/', p);
export const createVendorQuoteItem = (body) =>
  client.post('/inventory/vendor-quote-items/', withStoreCreateBody(body));
export const updateVendorQuoteItem = (itemId, body) =>
  client.patch(`/inventory/vendor-quote-items/${itemId}/`, withStoreUpdateBody(body));
export const deleteVendorQuoteItem = (itemId) =>
  client.delete(`/inventory/vendor-quote-items/${itemId}/`);

export const submitVendorQuote = (id, body = {}) => {
  const actor = getStoreActorId();
  const payload = { ...body };
  if (actor && !payload.submitted_by) payload.submitted_by = actor;
  return client.post(`/inventory/vendor-quotes/${id}/submit/`, payload);
};

/** @deprecated use submitVendorQuote */
export const submitQuote = submitVendorQuote;

export const withdrawVendorQuote = (id, body = {}) =>
  client.post(`/inventory/vendor-quotes/${id}/withdraw/`, body);

export const startQuoteEvaluation = (id) =>
  client.post(`/inventory/vendor-quotes/${id}/start-evaluation/`, {});

export const evaluateVendorQuote = (id, body) =>
  client.post(`/inventory/vendor-quotes/${id}/evaluate/`, withStoreUpdateBody(body));

/** Accept quote — POST /vendor-quotes/{id}/accept (NOT /accepted-quotes/) */
export const acceptVendorQuote = (quoteId, body, scope = {}) =>
  client.post(`/inventory/vendor-quotes/${quoteId}/accept/`, withStoreUpdateBody(body), scope);

/** @deprecated use acceptVendorQuote */
export const acceptQuote = acceptVendorQuote;

export const getQuoteEvaluation = (p) => client.get('/inventory/quote-evaluation/', p);
export const getAcceptedQuotes = (p) => client.get('/inventory/accepted-quotes/', p);
export const createAcceptedQuote = (body) =>
  client.post('/inventory/accepted-quotes/', withStoreCreateBody(body));
export const updateAcceptedQuote = (id, body) =>
  client.patch(`/inventory/accepted-quotes/${id}/`, withStoreUpdateBody(body));

export const markConvertedToPo = (acceptedQuoteId, scope = {}) =>
  client.post(`/inventory/accepted-quotes/${acceptedQuoteId}/mark-converted-to-po/`, {}, scope);

export { getStoreActorId };
